/**
 * Flat tree rows for template comparison & editor — mirrors how the app behaves:
 * sidebar → project list → project detail tabs → org-wide screens.
 */

import {
  P,
  RBAC_MODULES,
  ABAC_WORKSPACE_GROUPS,
  PERMISSION_LABELS,
  type PermissionKey,
} from './permission-keys';
import { PERMISSION_PREREQUISITES } from './permission-dependencies';

export type TemplateTreeAccent = 'blue' | 'violet' | 'emerald' | 'slate';

export interface TemplateTreeRow {
  id: string;
  depth: number;
  label: string;
  subtitle?: string;
  /** Section header — children hidden when id is in collapsed set */
  isSectionHeader?: boolean;
  accent?: TemplateTreeAccent;
  permissionKey?: PermissionKey;
  /** All keys must be on for "full"; some on = partial (amber) */
  aggregateKeys?: PermissionKey[];
}

function prereqHint(key: PermissionKey): string | undefined {
  const direct = PERMISSION_PREREQUISITES[key];
  if (!direct?.length) return undefined;
  const labels = direct.map((k) => PERMISSION_LABELS[k]);
  return `Requires: ${labels.join(' · ')}`;
}

/** Ordered rows for matrix + drawer tree (same source of truth). */
export function buildPermissionTemplateTreeRows(): TemplateTreeRow[] {
  const rows: TemplateTreeRow[] = [];

  rows.push({
    id: 'sec-org',
    depth: 0,
    isSectionHeader: true,
    accent: 'blue',
    label: 'Organization & admin',
    subtitle:
      'Global permissions — Settings, Users, Spark, org-wide time. No project open. Matches Settings in the sidebar.',
  });

  for (const mod of RBAC_MODULES) {
    rows.push({
      id: `org-mod-${mod.module}`,
      depth: 1,
      accent: 'blue',
      label: mod.label,
      subtitle: 'Module group',
    });
    for (const key of mod.keys) {
      rows.push({
        id: `org-key-${key}`,
        depth: 2,
        accent: 'blue',
        label: PERMISSION_LABELS[key],
        subtitle: prereqHint(key),
        permissionKey: key,
      });
    }
  }

  rows.push({
    id: 'sec-entry',
    depth: 0,
    isSectionHeader: true,
    accent: 'emerald',
    label: 'Open project',
    subtitle:
      'Lets someone open a project they are allowed to see — full detail (/projects/…) vs tasks-only (/my-projects/…) still depends on tab permissions below.',
  });

  rows.push({
    id: 'entry-view-proj',
    depth: 1,
    accent: 'emerald',
    label: PERMISSION_LABELS[P.PROJECTS_VIEW_PROJECT],
    subtitle: prereqHint(P.PROJECTS_VIEW_PROJECT),
    permissionKey: P.PROJECTS_VIEW_PROJECT,
  });

  rows.push({
    id: 'sec-workspace',
    depth: 0,
    isSectionHeader: true,
    accent: 'emerald',
    label: 'Inside the project',
    subtitle:
      'Maps to project tabs: Modules, Features, Tasks, Meetings, Team, Settings — plus time & timesheets. Only applies in projects the user can open.',
  });

  for (const group of ABAC_WORKSPACE_GROUPS) {
    rows.push({
      id: `ws-grp-${group.id}`,
      depth: 1,
      accent: 'emerald',
      label: group.heading,
      subtitle: 'Workspace area',
    });
    for (const section of group.rows) {
      rows.push({
        id: section.id,
        depth: 2,
        accent: 'emerald',
        label: section.label,
        subtitle:
          section.keys.length > 1
            ? `${section.keys.length} related permissions — all on for full access here`
            : prereqHint(section.keys[0]!),
        aggregateKeys: [...section.keys],
      });
    }
  }

  rows.push({
    id: 'sec-summary',
    depth: 0,
    isSectionHeader: true,
    accent: 'slate',
    label: 'Effective directory access',
    subtitle: 'Derived from "View assigned" and "View all projects" — not a separate toggle.',
  });

  rows.push({
    id: 'summary-effective',
    depth: 1,
    accent: 'slate',
    label: 'Summary',
    subtitle: 'All projects · Assigned only · None',
  });

  return rows;
}

export function filterTreeRowsByCollapse(
  rows: TemplateTreeRow[],
  collapsedSectionIds: ReadonlySet<string>,
): TemplateTreeRow[] {
  const out: TemplateTreeRow[] = [];
  let i = 0;
  while (i < rows.length) {
    const r = rows[i]!;
    out.push(r);
    if (r.isSectionHeader && collapsedSectionIds.has(r.id)) {
      const d = r.depth;
      i += 1;
      while (i < rows.length && rows[i]!.depth > d) {
        i += 1;
      }
      continue;
    }
    i += 1;
  }
  return out;
}
